<template>
    <div>
        <page-header :title="title" :items="items" />

        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <!-- Root category selector -->
                        <div class="form-group">
                            <h5>Top-level Category</h5>
                            <b-form-select
                                v-model="selectedRootId"
                                :options="rootCategories"
                                text-field="name"
                                value-field="id"
                            >
                                <template #first>
                                    <b-form-select-option
                                        :value="null"
                                        disabled
                                    >
                                        -- Select a category --
                                    </b-form-select-option>
                                </template>
                            </b-form-select>
                        </div>

                        <!-- Create new root category -->
                        <div class="mb-4">
                            <div class="form-group">
                                <label>New Top-level Category</label>
                                <input
                                    v-model="newRootName"
                                    type="text"
                                    class="form-control"
                                    placeholder="Enter category name"
                                />
                            </div>
                            <div class="form-group">
                                <label>Image (optional)</label>
                                <vue-dropzone
                                    id="newRootDropzone"
                                    ref="newRootDropzone"
                                    :options="imageDropzoneOptions"
                                    @vdropzone-complete="
                                        onRootImageUploaded
                                    "
                                />
                                <small
                                    v-if="newRootImage"
                                    class="text-success"
                                >
                                    Uploaded ✓
                                </small>
                            </div>
                            <button
                                type="button"
                                class="btn btn-success waves-effect waves-light"
                                :disabled="!newRootName.trim()"
                                @click="createRootCategory"
                            >
                                <i class="mdi mdi-plus-circle mr-1" />
                                Create
                            </button>
                        </div>

                        <hr v-if="selectedCategory" />

                        <!-- Breadcrumb -->
                        <div v-if="breadcrumb.length" class="mb-3">
                            <span
                                v-for="(node, idx) in breadcrumb"
                                :key="node.id"
                            >
                                <a
                                    href="#"
                                    @click.prevent="goToBreadcrumb(idx)"
                                >
                                    {{ node.name }}
                                </a>
                                <span
                                    v-if="idx !== breadcrumb.length - 1"
                                    class="mx-1"
                                >
                                    ›
                                </span>
                            </span>
                            <small class="text-muted ml-2">
                                (depth {{ currentDepth }} / {{ maxDepth }})
                            </small>
                        </div>

                        <!-- Category details -->
                        <div v-if="selectedCategory" class="mt-2">
                            <!-- Edit category name/image -->
                            <div class="mb-3">
                                <div class="form-group">
                                    <label>Category Name</label>
                                    <input
                                        v-model="selectedCategory.name"
                                        type="text"
                                        class="form-control"
                                    />
                                </div>
                                <div class="form-group">
                                    <label>Image (optional)</label>
                                    <vue-dropzone
                                        :id="`editDropzone-${selectedCategory.id}`"
                                        ref="editDropzone"
                                        :options="imageDropzoneOptions"
                                        @vdropzone-complete="
                                            onSelectedImageUploaded
                                        "
                                    />
                                    <button
                                        v-if="selectedCategory.image"
                                        type="button"
                                        class="btn btn-link btn-sm pl-0 mt-1"
                                        @click="
                                            selectedCategory.image = null
                                        "
                                    >
                                        <i class="far fa-trash-alt mr-1" />
                                        Remove image
                                    </button>
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        class="btn btn-primary waves-effect waves-light mr-2"
                                        @click="updateCategory"
                                    >
                                        <i class="fas fa-save mr-1" />
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-danger waves-effect waves-light"
                                        @click="showDeleteCategoryModal"
                                    >
                                        <i class="far fa-trash-alt mr-1" />
                                        Delete
                                    </button>
                                </div>
                            </div>

                            <div
                                v-if="selectedCategory.image"
                                class="mb-3"
                            >
                                <img
                                    :src="imageUrlFor(selectedCategory.image)"
                                    style="max-height: 120px; border: 1px solid #eee; border-radius: 4px;"
                                />
                            </div>

                            <hr />

                            <!-- Subcategories -->
                            <div>
                                <h5>Subcategories</h5>

                                <div
                                    v-if="selectedCategory.children && selectedCategory.children.length"
                                    class="table-responsive mb-3"
                                >
                                    <table class="table table-centered table-striped">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="child in selectedCategory.children"
                                                :key="child.id"
                                            >
                                                <td>{{ child.name }}</td>
                                                <td>
                                                    <a
                                                        href="#"
                                                        class="action-icon mr-3"
                                                        @click.prevent="
                                                            drillInto(child)
                                                        "
                                                    >
                                                        <i class="fas fa-folder-open" />
                                                        Open
                                                    </a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-else class="text-muted mb-3">
                                    No subcategories yet.
                                </div>
                            </div>

                            <div
                                v-if="canAddSubcategory"
                                class="mb-3"
                            >
                                <div class="form-group">
                                    <label>Add Subcategory</label>
                                    <input
                                        v-model="newChildName"
                                        type="text"
                                        class="form-control"
                                        placeholder="Subcategory name"
                                    />
                                </div>
                                <div class="form-group">
                                    <label>Image (optional)</label>
                                    <vue-dropzone
                                        id="newChildDropzone"
                                        ref="newChildDropzone"
                                        :options="imageDropzoneOptions"
                                        @vdropzone-complete="
                                            onChildImageUploaded
                                        "
                                    />
                                    <small
                                        v-if="newChildImage"
                                        class="text-success"
                                    >
                                        Uploaded ✓
                                    </small>
                                </div>
                                <button
                                    type="button"
                                    class="btn btn-success waves-effect waves-light"
                                    :disabled="!newChildName.trim()"
                                    @click="createSubcategory"
                                >
                                    <i class="mdi mdi-plus-circle mr-1" />
                                    Add Subcategory
                                </button>
                            </div>

                            <div
                                v-if="!canAddSubcategory"
                                class="text-muted mb-3"
                            >
                                <small>
                                    Max subcategory depth ({{ maxDepth }})
                                    reached.
                                </small>
                            </div>

                            <div>
                                <hr />

                                <!-- Files list -->
                                <h5>Content Items</h5>

                                <div
                                    v-if="
                                        selectedCategory.files &&
                                        selectedCategory.files.length
                                    "
                                    class="table-responsive mb-4"
                                >
                                    <table
                                        class="table table-centered table-striped"
                                    >
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Type</th>
                                                <th>URL</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="file in selectedCategory.files"
                                                :key="file.id"
                                            >
                                                <td>{{ file.name }}</td>
                                                <td>
                                                    <span
                                                        class="badge"
                                                        :class="badgeClass(file.type)"
                                                    >
                                                        {{ file.type }}
                                                    </span>
                                                </td>
                                                <td>
                                                    <a
                                                        :href="file.url"
                                                        target="_blank"
                                                        class="text-truncate d-inline-block"
                                                        style="max-width: 300px"
                                                    >
                                                        {{ file.url }}
                                                    </a>
                                                </td>
                                                <td>
                                                    <a
                                                        href="#"
                                                        class="action-icon text-danger"
                                                        @click.prevent="
                                                            deleteFile(file.id)
                                                        "
                                                    >
                                                        <i
                                                            class="far fa-trash-alt"
                                                        />
                                                    </a>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-else class="text-muted mb-4">
                                    No content items added yet.
                                </div>

                                <hr />

                                <!-- Add new file -->
                                <h5>Add Content Item</h5>
                                <div class="form-group col-12 col-md-6">
                                    <label>Name</label>
                                    <input
                                        v-model="newFile.name"
                                        type="text"
                                        class="form-control"
                                        placeholder="Enter content name"
                                    />
                                </div>
                                <div class="form-group col-12 col-md-6">
                                    <label>Type</label>
                                    <b-form-select
                                        v-model="newFile.type"
                                        :options="['PDF', 'Video', 'Link']"
                                    />
                                </div>
                                <div
                                    v-if="newFile.type !== 'Link'"
                                    class="form-group col-12 col-md-6"
                                >
                                    <label>Upload File</label>
                                    <vue-dropzone
                                        id="fileCategoryDropzone"
                                        ref="fileDropzone"
                                        :options="fileDropzoneOptions"
                                        class="h-100 d-flex justify-content-center align-items-center"
                                        @vdropzone-complete="uploadFile($event)"
                                    />
                                </div>
                                <div class="form-group col-12 col-md-6">
                                    <label>
                                        {{
                                            newFile.type === 'Link'
                                                ? 'URL'
                                                : 'Or paste a link'
                                        }}
                                    </label>
                                    <input
                                        v-model="newFile.url"
                                        type="text"
                                        class="form-control"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div class="form-group col-12">
                                    <button
                                        type="button"
                                        class="btn btn-success waves-effect waves-light"
                                        :disabled="!canAddFile"
                                        @click="addFile"
                                    >
                                        <i class="mdi mdi-plus-circle mr-1" />
                                        Add Content Item
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <confirm-modal
            id="confirmCategoryDeleteModal"
            title="Delete this category and everything inside it?"
            @agree="deleteCategory"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import vue2Dropzone from 'vue2-dropzone';
import config from '@config';
import ConfirmModal from '@components/elements/ConfirmModal';

const MAX_DEPTH = 4;

export default {
    page: {
        title: 'File Categories',
        meta: [{ name: 'description', content: config.description }]
    },

    components: {
        vueDropzone: vue2Dropzone,
        ConfirmModal
    },

    data() {
        return {
            title: 'File Categories',
            items: [
                { text: 'NovoCare', to: '/' },
                { text: 'File Categories', active: true }
            ],
            rootCategories: [],
            selectedRootId: null,
            selectedCategory: null,
            breadcrumb: [],
            newRootName: '',
            newRootImage: '',
            newChildName: '',
            newChildImage: '',
            newFile: {
                name: '',
                type: 'PDF',
                url: ''
            },
            maxDepth: MAX_DEPTH
        };
    },

    computed: {
        currentDepth() {
            return this.breadcrumb.length;
        },

        canAddSubcategory() {
            return this.currentDepth < MAX_DEPTH;
        },

        canAddFile() {
            if (!this.newFile.name) {
                return false;
            }

            if (this.newFile.type === 'Link') {
                return !!this.newFile.url;
            }

            return true;
        },

        fileDropzoneOptions() {
            return {
                url: config.apiUrl + '/file-categories/file/store',
                withCredentials: true,
                thumbnailWidth: 150,
                maxFilesize: 300,
                maxFiles: 1,
                acceptedFiles: '.pdf,.mp4,.mov,.avi,.wmv',
                dictDefaultMessage:
                    "Upload File <i class='fe-upload'></i>",
                addRemoveLinks: true
            };
        },

        imageDropzoneOptions() {
            return {
                url: config.apiUrl + '/file-categories/image/store',
                withCredentials: true,
                thumbnailWidth: 150,
                maxFilesize: 10,
                maxFiles: 1,
                acceptedFiles: 'image/*',
                dictDefaultMessage:
                    "Upload Image <i class='fe-upload'></i>",
                addRemoveLinks: true
            };
        },

        mediaBase() {
            return config.apiUrl.replace(/\/api\/?$/, '') + '/public';
        }
    },

    watch: {
        async selectedRootId(id) {
            if (!id) {
                this.selectedCategory = null;
                this.breadcrumb = [];

                return;
            }

            await this.loadCategory(id, true);
        }
    },

    async mounted() {
        await this.refreshRoots();
    },

    methods: {
        ...mapActions({
            getCategories: 'fileCategories/index',
            getCategory: 'fileCategories/show',
            storeCategory: 'fileCategories/store',
            updateCategoryAction: 'fileCategories/update',
            destroyCategory: 'fileCategories/destroy',
            addFileAction: 'fileCategories/addFile',
            deleteFileAction: 'fileCategories/deleteFile'
        }),

        async refreshRoots() {
            try {
                const all = await this.getCategories();

                this.rootCategories = all.filter(c => !c.parent_id);
            } catch (err) {
                console.error(err);
            }
        },

        async loadCategory(id, resetBreadcrumb = false) {
            try {
                const category = await this.getCategory(id);

                this.selectedCategory = category;

                if (resetBreadcrumb) {
                    this.breadcrumb = [
                        { id: category.id, name: category.name }
                    ];
                }
            } catch (err) {
                console.error(err);

                this.$bvToast.toast('Failed to load category.', {
                    title: 'Error!',
                    variant: 'danger',
                    solid: true
                });
            }
        },

        async drillInto(child) {
            this.breadcrumb.push({ id: child.id, name: child.name });
            await this.loadCategory(child.id);
        },

        async goToBreadcrumb(idx) {
            this.breadcrumb = this.breadcrumb.slice(0, idx + 1);
            await this.loadCategory(this.breadcrumb[idx].id);
        },

        async createRootCategory() {
            try {
                const category = await this.storeCategory({
                    name: this.newRootName.trim(),
                    image: this.newRootImage.trim() || null
                });

                this.rootCategories = [category, ...this.rootCategories];
                this.newRootName = '';
                this.newRootImage = '';
                this._resetDropzone('newRootDropzone');

                this.$bvToast.toast('Category created.', {
                    title: 'Success!',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this._toastError(err);
            }
        },

        async createSubcategory() {
            try {
                await this.storeCategory({
                    name: this.newChildName.trim(),
                    image: this.newChildImage.trim() || null,
                    parent_id: this.selectedCategory.id
                });

                this.newChildName = '';
                this.newChildImage = '';
                this._resetDropzone('newChildDropzone');

                await this.loadCategory(this.selectedCategory.id);

                this.$bvToast.toast('Subcategory added.', {
                    title: 'Success!',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this._toastError(err);
            }
        },

        async updateCategory() {
            try {
                const updated = await this.updateCategoryAction({
                    id: this.selectedCategory.id,
                    categoryData: {
                        name: this.selectedCategory.name,
                        image: this.selectedCategory.image || null
                    }
                });

                this._resetDropzone('editDropzone');

                const rootIndex = this.rootCategories.findIndex(
                    c => c.id === updated.id
                );

                if (rootIndex !== -1) {
                    this.rootCategories.splice(rootIndex, 1, updated);
                }

                const crumb = this.breadcrumb.find(
                    c => c.id === updated.id
                );

                if (crumb) {
                    crumb.name = updated.name;
                }

                this.$bvToast.toast('Category updated.', {
                    title: 'Success!',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this._toastError(err);
            }
        },

        showDeleteCategoryModal() {
            this.$bvModal.show('confirmCategoryDeleteModal');
        },

        async deleteCategory() {
            try {
                await this.destroyCategory(this.selectedCategory);

                const deletedId = this.selectedCategory.id;

                if (this.breadcrumb.length > 1) {
                    this.breadcrumb.pop();
                    await this.loadCategory(
                        this.breadcrumb[this.breadcrumb.length - 1].id
                    );
                } else {
                    this.selectedCategory = null;
                    this.breadcrumb = [];
                    this.selectedRootId = null;
                    this.rootCategories = this.rootCategories.filter(
                        c => c.id !== deletedId
                    );
                }

                this.$bvToast.toast('Category deleted.', {
                    title: 'Success!',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this._toastError(err);
            }
        },

        async addFile() {
            try {
                const file = await this.addFileAction({
                    categoryId: this.selectedCategory.id,
                    file: { ...this.newFile }
                });

                this.selectedCategory.files.push(file);
                this.clearNewFile();

                this.$bvToast.toast('Content item added.', {
                    title: 'Success!',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this._toastError(err);
            }
        },

        async deleteFile(fileId) {
            try {
                await this.deleteFileAction(fileId);

                this.selectedCategory.files =
                    this.selectedCategory.files.filter(f => f.id !== fileId);

                this.$bvToast.toast('Content item deleted.', {
                    title: 'Success!',
                    variant: 'success',
                    solid: true
                });
            } catch (err) {
                this._toastError(err);
            }
        },

        uploadFile(e) {
            const ref = this.$refs.fileDropzone;

            if (ref.dropzone.files.length > 1) {
                ref.dropzone.removeFile(ref.dropzone.files[0]);
            }

            if (!e.xhr) {
                return;
            }

            this.newFile.url = e.xhr.response;
        },

        onRootImageUploaded(e) {
            const ref = this.$refs.newRootDropzone;
            if (ref && ref.dropzone.files.length > 1) {
                ref.dropzone.removeFile(ref.dropzone.files[0]);
            }
            if (!e.xhr) return;
            this.newRootImage = e.xhr.response;
        },

        onChildImageUploaded(e) {
            const ref = this.$refs.newChildDropzone;
            if (ref && ref.dropzone.files.length > 1) {
                ref.dropzone.removeFile(ref.dropzone.files[0]);
            }
            if (!e.xhr) return;
            this.newChildImage = e.xhr.response;
        },

        onSelectedImageUploaded(e) {
            const ref = this.$refs.editDropzone;
            if (ref && ref.dropzone.files.length > 1) {
                ref.dropzone.removeFile(ref.dropzone.files[0]);
            }
            if (!e.xhr) return;
            this.$set(this.selectedCategory, 'image', e.xhr.response);
        },

        imageUrlFor(value) {
            if (!value) return '';
            if (value.startsWith('http')) return value;
            return `${this.mediaBase}/${value}`;
        },

        clearNewFile() {
            this.newFile = { name: '', type: 'PDF', url: '' };

            const ref = this.$refs.fileDropzone;

            if (ref && ref.dropzone.files.length) {
                ref.dropzone.removeFile(ref.dropzone.files[0]);
            }
        },

        badgeClass(type) {
            if (type === 'PDF') return 'badge-danger';
            if (type === 'Video') return 'badge-info';
            if (type === 'Link') return 'badge-success';

            return 'badge-secondary';
        },

        _toastError(err) {
            console.error(err);

            const message =
                err.response && err.response.data
                    ? typeof err.response.data === 'string'
                        ? err.response.data
                        : err.response.data.message
                    : 'Something went wrong!';

            this.$bvToast.toast(message, {
                title: 'Error!',
                variant: 'danger',
                solid: true
            });
        },

        _resetDropzone(refName) {
            const ref = this.$refs[refName];

            if (ref && ref.dropzone) {
                ref.dropzone.removeAllFiles();
            }
        }
    }
};
</script>
